import { useCallback, useEffect, lazy, Suspense, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslations from '../../translation/use-translations';
import GameInfo from '~/components/casino-shared/game-info/game-info';
import useIntervalAction from '../../../hooks/use-interval-action.js';
import WeSpinEventCard from './we-spin-event-card.js';
import WeSpinClipCard from './we-spin-clip-card.js';
import useResources from '../../resource/use-resources.js';
import {
	closeOnboarding,
	openOnboarding,
	setOnboardingDisabled,
	selectIsOnboardingOpen
} from '../../onboarding/onboarding-duck.js';
import {
	getOnboardingClosedCookie,
	getOnboardingDisabledCookie,
	setOnboardingClosedCookie
} from '../../onboarding/onboarding-cookie-util.js';
import {
	WE_SPIN_ONBOARDING_CLOSED_COOKIE,
	WE_SPIN_ONBOARDING_DISABLED_COOKIE
} from './constants/we-spin-cookie-names.js';
import WeSpinLobbyCardTutorial from './we-spin-lobby-card-tutorial.js';
import { RESOURCE_KEYS } from './constants/we-spin-resource-keys.js';
import { WE_SPIN_FRIENDS_RESOURCE_KEYS } from './constants/we-spin-friends-resource-keys.js';
import { fetchSessions, FETCH_SESSIONS, fetchClips, fetchPrivateSessions } from './ducks/we-spin-duck.js';
import {
	selectSessions,
	selectSessionsLoaded,
	selectClips,
	selectPrivateSessions,
	selectConfiguration
} from './ducks/we-spin-selectors.js';
import { WE_SPIN_ALL_TEXT_KEYS } from './constants/we-spin-text-keys.js';
import { updateMainContentLoading } from '~/components/brand/ducks/index.js';
import { fetchIsBlockedStreamer, selectIsStreamer } from './ducks/we-spin-dashboard-duck.js';
import WeSpinCreateEventCard from './we-spin-create-event-card.js';
import WeSpinSessionLobbyCarousel from './we-spin-session-lobby-carousel';
import Loading from '~/components/misc/loading';
import { UPCOMING_STATES } from './constants/we-spin-stream-states';

const FALLBACK = <div className="spinner" />;

const WeSpinOnboardingContainer = lazy(() =>
	import(
		/* webpackChunkName: "wespin-onboarding" */
		'./we-spin-onboarding-container.js'
	)
);
const WeSpinFriendsOnboardingContainer = lazy(() =>
	import(
		/* webpackChunkName: "wespin-onboarding" */
		'./we-spin-friends-onboarding-container.js'
	)
);

const getEventsFromSessions = sessions =>
	sessions
		.reduce((acc, cur) => [...acc, ...cur.streamViews], [])
		.filter(event => UPCOMING_STATES.includes(event.state))
		.sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

const WeSpinLobby = () => {
	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_ALL_TEXT_KEYS);
	const configuration = useSelector(selectConfiguration);
	const sessions = useSelector(selectSessions);
	const privateSessions = useSelector(selectPrivateSessions);
	const sessionsLoaded = useSelector(selectSessionsLoaded);
	const isOnboardingOpen = useSelector(selectIsOnboardingOpen);
	const clips = useSelector(selectClips);
	const isStreamer = useSelector(selectIsStreamer);
	const [onboardingType, setOnboardingType] = useState(null);
	const { resources, isLoading: isLoadingResources } = useResources(RESOURCE_KEYS);
	const { resources: friendsResources, isLoading: isLoadingFriendsResources } =
		useResources(WE_SPIN_FRIENDS_RESOURCE_KEYS);

	const { hasWeSpinOnboarding, hasWeSpinClip, hasWeSpinFriends, weSpinViewListPollInterval } = configuration;

	const events = useMemo(() => getEventsFromSessions(sessions), [sessions]);
	const privateEvents = useMemo(() => getEventsFromSessions(privateSessions), [privateSessions]);

	useIntervalAction(
		FETCH_SESSIONS,
		dispatch => {
			dispatch(fetchSessions());
			if (hasWeSpinFriends) {
				dispatch(fetchPrivateSessions());
			}
		},
		weSpinViewListPollInterval || 5000
	);

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(fetchIsBlockedStreamer());
	}, [dispatch]);

	useEffect(() => {
		if (hasWeSpinClip) {
			dispatch(fetchClips());
		}
	}, [dispatch, hasWeSpinClip]);

	const handleCloseOnboarding = useCallback(
		e => {
			if (e) {
				e.stopPropagation();
			}
			dispatch(closeOnboarding());
			setOnboardingClosedCookie(WE_SPIN_ONBOARDING_CLOSED_COOKIE);
		},
		[dispatch]
	);

	const handleOpenOnboarding = useCallback(
		(showOnboardingType, e) => {
			if (showOnboardingType === 'friends') {
				setOnboardingType('friends');
			} else {
				setOnboardingType('regular');
			}
			if (e) {
				e.stopPropagation();
			}
			dispatch(openOnboarding());
		},
		[dispatch]
	);

	useEffect(() => {
		if (!hasWeSpinOnboarding) return;
		const onboardingIsDisabledCookie = getOnboardingDisabledCookie(WE_SPIN_ONBOARDING_DISABLED_COOKIE);
		const onboardingIsClosedCookie = getOnboardingClosedCookie(WE_SPIN_ONBOARDING_CLOSED_COOKIE);
		dispatch(setOnboardingDisabled(onboardingIsDisabledCookie));
		if (onboardingIsClosedCookie || onboardingIsDisabledCookie) {
			dispatch(closeOnboarding());
		} else {
			dispatch(openOnboarding());
		}
		dispatch(updateMainContentLoading('loaded'));
	}, [dispatch, hasWeSpinOnboarding]);

	if (isLoadingTranslations || isLoadingResources || isLoadingFriendsResources) return <Loading />;

	return (
		<>
			{hasWeSpinOnboarding && isOnboardingOpen && onboardingType === 'regular' && (
				<Suspense fallback={FALLBACK}>
					<WeSpinOnboardingContainer onCloseOnboarding={handleCloseOnboarding} resources={resources} />
				</Suspense>
			)}
			{hasWeSpinOnboarding && isOnboardingOpen && onboardingType === 'friends' && (
				<Suspense fallback={FALLBACK}>
					<WeSpinFriendsOnboardingContainer
						onCloseOnboarding={handleCloseOnboarding}
						resources={friendsResources}
					/>
				</Suspense>
			)}

			<GameInfo />

			<WeSpinSessionLobbyCarousel />

			<div className="game-grid game-grid-we-spin u-p1">
				{hasWeSpinFriends && (
					<WeSpinCreateEventCard
						key="create-event-card"
						onClick={() => handleOpenOnboarding('friends')}
						resources={friendsResources}
					/>
				)}
				{hasWeSpinOnboarding && hasWeSpinFriends && sessionsLoaded && (
					<WeSpinLobbyCardTutorial
						onClick={() => handleOpenOnboarding('friends')}
						translations={translations}
						resources={friendsResources}
						cssOrder={999}
						isFriends
					/>
				)}
				{hasWeSpinFriends &&
					privateEvents.map((event, i) => (
						<WeSpinEventCard
							key={event.streamRef}
							translations={translations}
							event={event}
							resources={resources}
							cssOrder={i + 1}
						/>
					))}
				{events?.map((event, i) => (
					<WeSpinEventCard
						key={event.streamRef}
						translations={translations}
						event={event}
						resources={resources}
						cssOrder={i + 1}
					/>
				))}
				{hasWeSpinOnboarding && sessionsLoaded && (
					<WeSpinLobbyCardTutorial
						onClick={() => handleOpenOnboarding('regular')}
						translations={translations}
						resources={resources}
						cssOrder={999}
					/>
				)}
				{hasWeSpinClip && (isStreamer || !!clips.length) && (
					<WeSpinClipCard key="clip-card" translations={translations} clips={clips} />
				)}
			</div>
		</>
	);
};

export default WeSpinLobby;
